/**
 * CriticalThinkingPage — Wonder-question workspace for children.
 * Route: /critical-thinking/:childId
 *
 * Flow: tap "Give me a wonder question" → child sees a Big Question + hints →
 * types their own guess → AI gently explains the concept and invites a follow-up
 * → child can ask any follow-up question in a mini chat. All exchanges persist.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import BadgeCelebration from '@/components/BadgeCelebration';
import {
  ArrowLeft, Brain, Sparkles, Lightbulb, Send, Loader2, MessageCircle, RefreshCw, PenLine, ScrollText,
} from 'lucide-react';

const TOPICS = [
  { key: 'science', label: '🔬 Science' },
  { key: 'nature', label: '🌱 Nature' },
  { key: 'space', label: '🚀 Space' },
  { key: 'math', label: '➕ Math' },
  { key: 'society', label: '👨‍👩‍👧 Society' },
  { key: 'art', label: '🎨 Art' },
];

const QUALITY_META = {
  starter: { label: 'Good start!', color: 'bg-sky-100 text-sky-700 border-sky-200' },
  thoughtful: { label: 'Thoughtful thinking!', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  excellent: { label: 'Amazing reasoning!', color: 'bg-amber-100 text-amber-700 border-amber-200' },
};

const CriticalThinkingPage = () => {
  const { childId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();

  const [prompt, setPrompt] = useState(null);   // {id, question, concept, hints, turns, ...}
  const [loading, setLoading] = useState(false);
  const [topic, setTopic] = useState('');
  const [difficulty, setDifficulty] = useState('medium');
  const [childAnswer, setChildAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [followupText, setFollowupText] = useState('');
  const [followingUp, setFollowingUp] = useState(false);
  const [celebrationBadge, setCelebrationBadge] = useState(null);
  const [showHint, setShowHint] = useState(-1);
  const [showLog, setShowLog] = useState(false);
  const [log, setLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(false);
  const chatEndRef = useRef(null);

  const openLog = async () => {
    setShowLog(true);
    setLoadingLog(true);
    try {
      const res = await api.get(`/critical-thinking/history/${childId}`);
      setLog(res.data.sessions || []);
    } catch (err) {
      toast.error('Could not load curiosity log');
    } finally {
      setLoadingLog(false);
    }
  };

  const openLoggedSession = async (sessionId) => {
    setShowLog(false);
    setLoading(true);
    try {
      const s = await api.get(`/critical-thinking/session/${sessionId}`);
      setPrompt(s.data);
      setFeedback(null);
      setChildAnswer('');
      setShowHint(-1);
    } catch (err) { toast.error('Could not open that session'); }
    finally { setLoading(false); }
  };

  const scrollChatToEnd = () => setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

  const generatePrompt = async () => {
    setLoading(true);
    setFeedback(null);
    setChildAnswer('');
    setShowHint(-1);
    try {
      const res = await api.post('/critical-thinking/generate', {
        child_id: childId,
        topic: topic || undefined,
        difficulty,
      });
      setPrompt(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not generate a question. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const submitAnswer = async () => {
    if (!prompt || !childAnswer.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post('/critical-thinking/answer', {
        session_id: prompt.id,
        child_answer: childAnswer,
      });
      setFeedback(res.data);
      // Refresh session to include the new turns
      const s = await api.get(`/critical-thinking/session/${prompt.id}`);
      setPrompt(s.data);
      setChildAnswer('');
      scrollChatToEnd();
    } catch (err) {
      toast.error('Could not send your answer. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitFollowup = async () => {
    if (!prompt || !followupText.trim()) return;
    setFollowingUp(true);
    try {
      const res = await api.post('/critical-thinking/followup', {
        session_id: prompt.id,
        question: followupText,
      });
      const s = await api.get(`/critical-thinking/session/${prompt.id}`);
      setPrompt(s.data);
      setFollowupText('');
      if (res.data?.badge_awarded) {
        setCelebrationBadge({
          id: res.data.badge_awarded.id,
          label: res.data.badge_awarded.label,
          emoji: res.data.badge_awarded.emoji,
        });
      }
      scrollChatToEnd();
    } catch (err) {
      toast.error('Could not send your follow-up. Try again.');
    } finally {
      setFollowingUp(false);
    }
  };

  // Auto-generate a question on first mount so the child lands in the "playing" state
  useEffect(() => {
    if (childId && !prompt && !loading) generatePrompt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  const turns = prompt?.turns || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-fuchsia-50 to-amber-50">
      <BadgeCelebration badge={celebrationBadge} onClose={() => setCelebrationBadge(null)} />
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-indigo-200">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="ct-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <Brain className="w-6 h-6 text-fuchsia-500" />
            <h1 className="font-fredoka text-xl font-bold text-slate-800">Wonder Room</h1>
          </div>
          <div className="ml-auto flex items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            onClick={openLog}
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            data-testid="ct-open-log-btn"
          >
            <ScrollText className="w-4 h-4 mr-1" /> My log
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={generatePrompt}
            disabled={loading}
            className="ml-2 border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"
            data-testid="ct-new-question-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            New question
          </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Topic + difficulty */}
        <div className="flex flex-wrap gap-2 items-center" data-testid="ct-controls">
          <button
            type="button"
            onClick={() => setTopic('')}
            className={`px-3 py-1.5 text-xs rounded-full border-2 font-semibold ${topic === '' ? 'border-fuchsia-500 bg-fuchsia-100 text-fuchsia-700' : 'border-slate-200 bg-white text-slate-600'}`}
          >
            🎲 Anything
          </button>
          {TOPICS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTopic(topic === t.key ? '' : t.key)}
              className={`px-3 py-1.5 text-xs rounded-full border-2 font-semibold ${topic === t.key ? 'border-fuchsia-500 bg-fuchsia-100 text-fuchsia-700' : 'border-slate-200 bg-white text-slate-600'}`}
              data-testid={`ct-topic-${t.key}`}
            >
              {t.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-1 text-xs">
            {['easy', 'medium', 'hard'].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDifficulty(d)}
                className={`px-3 py-1.5 rounded-full border font-semibold capitalize ${difficulty === d ? 'border-indigo-500 bg-indigo-100 text-indigo-700' : 'border-slate-200 bg-white text-slate-500'}`}
                data-testid={`ct-diff-${d}`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        {/* Big wonder question */}
        {loading && !prompt && (
          <div className="p-10 flex items-center justify-center text-fuchsia-500">
            <Loader2 className="w-8 h-8 animate-spin mr-2" /> Thinking of a good question...
          </div>
        )}

        {prompt && (
          <Card className="border-fuchsia-200 shadow-lg bg-white/90">
            <CardContent className="p-6">
              <p className="text-[11px] font-bold uppercase tracking-widest text-fuchsia-500 mb-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Wonder Question
              </p>
              <h2 className="font-fredoka text-2xl md:text-3xl font-bold text-slate-800 leading-tight" data-testid="ct-question-text">
                {prompt.question}
              </h2>
              {prompt.concept && (
                <p className="text-xs text-slate-500 mt-2">Concept: <span className="font-semibold">{prompt.concept}</span></p>
              )}

              {/* Hints */}
              {(prompt.hints || []).length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-xs font-semibold text-slate-600 flex items-center gap-1">
                    <Lightbulb className="w-3.5 h-3.5 text-amber-500" /> Stuck? Peek at a hint
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(prompt.hints || []).map((h, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setShowHint(i)}
                        className={`px-3 py-1.5 text-xs rounded-full border font-semibold ${showHint >= i ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-white text-slate-500 border-slate-200 hover:bg-amber-50'}`}
                        data-testid={`ct-hint-${i}`}
                      >
                        Hint {i + 1}
                      </button>
                    ))}
                  </div>
                  {showHint >= 0 && (
                    <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                      {prompt.hints[showHint]}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Conversation */}
        {turns.length > 0 && (
          <div className="space-y-3" data-testid="ct-conversation">
            {turns.map((t, i) => (
              <div key={i} className={`flex ${t.role === 'child' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${
                    t.role === 'child'
                      ? 'bg-fuchsia-500 text-white rounded-br-sm'
                      : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm'
                  }`}
                  data-testid={`ct-turn-${t.role}-${i}`}
                >
                  {t.role === 'ai' && t.meta?.kind === 'feedback' && t.meta?.quality && (
                    <p className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border mb-2 ${QUALITY_META[t.meta.quality]?.color || ''}`}>
                      {QUALITY_META[t.meta.quality]?.label || ''}
                    </p>
                  )}
                  <p className="whitespace-pre-wrap">{t.text}</p>
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
        )}

        {/* Child answer input — shown until first answer submitted */}
        {prompt && !feedback && turns.length === 0 && (
          <Card className="border-indigo-200">
            <CardContent className="p-4">
              <p className="text-sm font-semibold text-slate-700 flex items-center gap-1 mb-2">
                <PenLine className="w-4 h-4 text-indigo-500" /> What do YOU think?
              </p>
              <Textarea
                value={childAnswer}
                onChange={(e) => setChildAnswer(e.target.value)}
                placeholder="Type your best guess — there are no wrong answers here!"
                rows={4}
                className="resize-none"
                data-testid="ct-answer-input"
              />
              <div className="flex justify-end mt-3">
                <Button
                  onClick={submitAnswer}
                  disabled={submitting || !childAnswer.trim()}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white"
                  data-testid="ct-answer-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                  Share my thinking
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Follow-up chat after first answer */}
        {prompt && turns.length > 0 && (
          <Card className="border-emerald-200">
            <CardContent className="p-4">
              <p className="text-sm font-semibold text-slate-700 flex items-center gap-1 mb-2">
                <MessageCircle className="w-4 h-4 text-emerald-500" /> Ask a follow-up question
              </p>
              <div className="flex gap-2">
                <Textarea
                  value={followupText}
                  onChange={(e) => setFollowupText(e.target.value)}
                  placeholder="e.g. But what if it happens at night?"
                  rows={2}
                  className="resize-none flex-1"
                  data-testid="ct-followup-input"
                />
                <Button
                  onClick={submitFollowup}
                  disabled={followingUp || !followupText.trim()}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white shrink-0"
                  data-testid="ct-followup-submit"
                >
                  {followingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Curiosity Log drawer */}
      {showLog && (
        <div
          className="fixed inset-0 z-[80] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={() => setShowLog(false)}
          data-testid="ct-log-backdrop"
        >
          <div
            className="w-full max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-xl max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-indigo-500" />
                <h2 className="font-fredoka text-lg font-bold text-slate-800">My Curiosity Log</h2>
              </div>
              <button
                type="button"
                onClick={() => setShowLog(false)}
                className="text-slate-400 hover:text-slate-700 text-xl px-2"
                aria-label="Close log"
              >
                ×
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-4 space-y-2" data-testid="ct-log-list">
              {loadingLog && (
                <div className="flex items-center justify-center p-6 text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
              )}
              {!loadingLog && log.length === 0 && (
                <p className="text-center text-slate-500 py-8">
                  No wonders yet — tap &ldquo;New question&rdquo; to explore your first one! 🌟
                </p>
              )}
              {log.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => openLoggedSession(s.id)}
                  className="w-full text-left rounded-xl border border-slate-200 p-3 hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                  data-testid={`ct-log-item-${s.id}`}
                >
                  <p className="font-semibold text-slate-800 text-sm">{s.question}</p>
                  <p className="text-xs text-slate-500 mt-1 flex items-center gap-2">
                    <span className="capitalize">{s.topic}</span>
                    <span>•</span>
                    <span>{s.turn_count || 0} turn{(s.turn_count || 0) === 1 ? '' : 's'}</span>
                    {s.last_quality && (
                      <>
                        <span>•</span>
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${QUALITY_META[s.last_quality]?.color || ''}`}>
                          {QUALITY_META[s.last_quality]?.label || s.last_quality}
                        </span>
                      </>
                    )}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CriticalThinkingPage;
